import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import MapEngine from '../components/MapEngine';
import DATA_TYPES from '../config/dataTypes';
import { API_URL } from '../components/config.js';
import axios from 'axios';
import './UnifiedMapViewPage.css';

/**
 * UnifiedMapViewPage Component
 * 
 * Displays all community data types on a single map with layer toggles.
 * Public users see approved data, staff/admin see everything.
 */
const UnifiedMapViewPage = () => {
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [dataLayers, setDataLayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const isStaff = currentUser && (currentUser.userType === 'staff' || currentUser.userType === 'admin');

  const loadAllData = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const dataTypeIds = Object.keys(DATA_TYPES);
      const headers = currentUser?.token ? { Authorization: `Bearer ${currentUser.token}` } : {};

      // Fetch data for all data types in parallel
      const responses = await Promise.all(
        dataTypeIds.map(dataTypeId =>
          axios.get(`${API_URL}/api/data/${dataTypeId}`, { headers })
            .then(res => ({ 
              dataTypeId, 
              dataType: DATA_TYPES[dataTypeId],
              data: res.data 
            }))
            .catch(err => {
              console.error(`Error loading ${dataTypeId}:`, err);
              return { 
                dataTypeId, 
                dataType: DATA_TYPES[dataTypeId],
                data: [] 
              };
            })
        )
      );

      setDataLayers(responses);
    } catch (err) {
      console.error('Error loading map data:', err);
      setError('Error loading map data');
    } finally {
      setLoading(false);
    }
  }, [currentUser?.token]);

  // Load all data types
  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  if (loading) {
    return (
      <div className="unified-map-page loading">
        <div className="spinner"></div>
        <p>Loading map data...</p>
      </div>
    );
  }

  return (
    <div className="unified-map-page">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h1>🗺️ Community Data Map</h1>
            <p>Explore all community-contributed data on one map</p>
          </div>
          <div className="header-actions">
            <button 
              className="btn btn-primary"
              onClick={() => navigate('/data/collection')}
            >
              📝 Collect Data
            </button>
            {isStaff && (
              <button 
                className="btn btn-secondary"
                onClick={() => navigate('/staff/data-types')}
              >
                ⚙️ Manage Data Types
              </button>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger">
          {error}
        </div>
      )}

      <div className="map-container">
        <MapEngine
          dataLayers={dataLayers}
          height="calc(100vh - 180px)"
          onFeatureClick={(item) => {
            console.log('Feature clicked:', item);
            // Could open a modal or navigate to detail page
          }}
        />
      </div>

      <div className="data-info">
        <h3>About This Map</h3>
        <div className="info-grid">
          {Object.values(DATA_TYPES).map(dataType => {
            const layer = dataLayers.find(l => l.dataTypeId === dataType.id);
            const count = layer?.data?.length || 0;
            const icon = typeof dataType.icon === 'string' ? dataType.icon : '📍';

            return (
              <div key={dataType.id} className="info-card">
                <span className="info-icon">{icon}</span>
                <div className="info-content">
                  <h4>{dataType.name || 'Unknown'}</h4>
                  <p>{count} {count === 1 ? 'item' : 'items'}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default UnifiedMapViewPage;
